// Sprint 3 — Mejoras al Booking Wizard público
import { css } from "styled-system/css";
import { useCallback, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { formatInTimeZone } from "date-fns-tz";
import { es } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { useToast } from "@/hooks/useToast";
import { displayApiError } from "@/lib/client-safe-error";

type ManageAppt = {
  id: string;
  serviceId: string;
  cancelToken: string;
  clientName: string;
  clientEmail: string;
  serviceName: string;
  professionalName: string;
  professionalSlug: string;
  timezone: string;
  startTime: string;
  endTime: string;
  status: string;
  allowClientCancelReschedule: boolean;
  hoursBeforeCancel: number;
  minHoursBeforeReschedule: number;
};

type ManageResponse = {
  mode: "single" | "list";
  policy: {
    allowClientCancelReschedule: boolean;
    hoursBeforeCancel: number;
    minHoursBeforeReschedule: number;
  };
  appointments: ManageAppt[];
};

export function ManageBookingPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [tab, setTab] = useState<"token" | "email">("token");
  const [tokenInput, setTokenInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ManageResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [cancelTarget, setCancelTarget] = useState<ManageAppt | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const search = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    setData(null);
    setCancelTarget(null);
    try {
      const u = new URL("/api/public/bookings/manage", window.location.origin);
      u.searchParams.set("slug", slug);
      if (tab === "token") {
        const t = tokenInput.trim();
        if (!t) {
          setError("Ingresá el código de cancelación.");
          setLoading(false);
          return;
        }
        u.searchParams.set("token", t);
      } else {
        const e = emailInput.trim();
        if (!e) {
          setError("Ingresá tu email.");
          setLoading(false);
          return;
        }
        u.searchParams.set("email", e);
      }
      const r = await fetch(u.toString());
      const j = (await r.json()) as { error?: string } & Partial<ManageResponse>;
      if (!r.ok) {
        setError(displayApiError(j.error, "No se pudo buscar."));
        return;
      }
      setData(j as ManageResponse);
      if (!(j as ManageResponse).appointments?.length) {
        setError("No encontramos turnos futuros con esos datos.");
      }
    } catch {
      setError("Error de red. Probá de nuevo.");
    } finally {
      setLoading(false);
    }
  }, [slug, tab, tokenInput, emailInput]);

  async function confirmCancel() {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const r = await fetch("/api/public/bookings/cancel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: cancelTarget.cancelToken }),
      });
      const j = (await r.json()) as { error?: string; ok?: boolean; refundMessage?: string };
      if (!r.ok) {
        toast.error(j.error ?? "No se pudo cancelar.");
        return;
      }
      toast.success(j.refundMessage ?? "Turno cancelado.");
      setCancelTarget(null);
      setData(null);
    } catch {
      toast.error("Error al cancelar.");
    } finally {
      setCancelling(false);
    }
  }

  function goReschedule(a: ManageAppt) {
    if (!slug) return;
    navigate(`/book/${slug}`, {
      replace: true,
      state: {
        reschedule: {
          appointmentId: a.id,
          cancelToken: a.cancelToken,
          serviceId: a.serviceId,
          clientName: a.clientName,
          clientEmail: a.clientEmail,
          clientPhone: "",
        },
      },
    });
  }

  if (!slug) {
    return <p className={css({ p: "6" })}>Ruta inválida.</p>;
  }

  return (
    <div className={css({ minH: "100vh", bg: "bg.muted", p: { base: "4", md: "8" } })}>
      <div className={css({ maxW: "36rem", mx: "auto" })}>
        <Link
          to={`/book/${slug}`}
          className={css({ fontSize: "sm", color: "accent.DEFAULT", textDecoration: "none", display: "inline-block", mb: "4" })}
        >
          ← Volver a reservar
        </Link>
        <h1 className={css({ fontSize: "2xl", fontWeight: "bold", mb: "2" })}>Gestionar mi turno</h1>
        <p className={css({ fontSize: "sm", color: "fg.muted", mb: "6" })}>
          Buscá con el código que recibiste por email o con el email con el que reservaste.
        </p>

        <div className={css({ display: "flex", gap: "2", mb: "4" })}>
          <button
            type="button"
            onClick={() => setTab("token")}
            className={css({
              px: "4",
              py: "2",
              rounded: "lg",
              fontSize: "sm",
              fontWeight: "semibold",
              border: "none",
              cursor: "pointer",
              bg: tab === "token" ? "brand.100" : "bg.card",
              color: tab === "token" ? "accent.DEFAULT" : "fg.muted",
            })}
          >
            Código
          </button>
          <button
            type="button"
            onClick={() => setTab("email")}
            className={css({
              px: "4",
              py: "2",
              rounded: "lg",
              fontSize: "sm",
              fontWeight: "semibold",
              border: "none",
              cursor: "pointer",
              bg: tab === "email" ? "brand.100" : "bg.card",
              color: tab === "email" ? "accent.DEFAULT" : "fg.muted",
            })}
          >
            Email
          </button>
        </div>

        <div className={css({ display: "flex", flexDir: "column", gap: "3", mb: "6" })}>
          {tab === "token" ? (
            <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
              <span className={css({ fontSize: "sm", color: "fg.muted" })}>Código de cancelación</span>
              <Input value={tokenInput} onChange={(e) => setTokenInput(e.target.value)} placeholder="Pegá el token del mail" />
            </label>
          ) : (
            <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
              <span className={css({ fontSize: "sm", color: "fg.muted" })}>Email</span>
              <Input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="tu@email.com"
              />
            </label>
          )}
          <Button type="button" variant="primary" disabled={loading} onClick={() => void search()}>
            {loading ? "Buscando…" : "Buscar"}
          </Button>
        </div>

        {error ? <p className={css({ color: "red.600", fontSize: "sm", mb: "4" })}>{error}</p> : null}
        {loading ? <LoadingFrame variant="light" minHeight={120} /> : null}

        {data?.appointments?.length ? (
          <ul className={css({ display: "flex", flexDir: "column", gap: "4", listStyle: "none", p: 0, m: 0 })}>
            {data.appointments.map((a) => (
              <li
                key={a.id}
                className={css({
                  p: "5",
                  rounded: "xl",
                  borderWidth: "1px",
                  borderColor: "border.DEFAULT",
                  bg: "bg.card",
                  boxShadow: "sm",
                })}
              >
                <p className={css({ fontWeight: "semibold", mb: "1" })}>{a.serviceName}</p>
                <p className={css({ fontSize: "sm", color: "fg.muted", mb: "1" })}>
                  {formatInTimeZone(new Date(a.startTime), a.timezone, "EEEE d MMM yyyy · HH:mm", { locale: es })}
                </p>
                <p className={css({ fontSize: "sm", color: "fg.muted" })}>
                  {a.clientName} · {a.clientEmail}
                </p>
                {!data.policy.allowClientCancelReschedule ? (
                  <p className={css({ fontSize: "xs", color: "fg.muted", mt: "3" })}>
                    El profesional no habilitó cancelación o reprogramación online.
                  </p>
                ) : (
                  <div className={css({ display: "flex", flexWrap: "wrap", gap: "2", mt: "4" })}>
                    <Button type="button" variant="secondary" onClick={() => goReschedule(a)}>
                      Reprogramar
                    </Button>
                    <Button type="button" variant="secondary" onClick={() => setCancelTarget(a)}>
                      Cancelar turno
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : null}

        {cancelTarget ? (
          <div
            className={css({
              mt: "6",
              p: "5",
              rounded: "xl",
              borderWidth: "2px",
              borderColor: "amber.300",
              bg: "amber.50",
            })}
          >
            <p className={css({ fontWeight: "semibold", mb: "3" })}>¿Seguro que querés cancelar este turno?</p>
            <p className={css({ fontSize: "sm", color: "fg.muted", mb: "4" })}>
              {cancelTarget.serviceName} —{" "}
              {formatInTimeZone(new Date(cancelTarget.startTime), cancelTarget.timezone, "d/M/yyyy HH:mm", {
                locale: es,
              })}
            </p>
            <div className={css({ display: "flex", gap: "2", flexWrap: "wrap" })}>
              <Button type="button" variant="primary" disabled={cancelling} onClick={() => void confirmCancel()}>
                {cancelling ? "Cancelando…" : "Sí, cancelar"}
              </Button>
              <Button type="button" variant="secondary" disabled={cancelling} onClick={() => setCancelTarget(null)}>
                No, volver
              </Button>
            </div>
          </div>
        ) : null}

      </div>
    </div>
  );
}
